// js/PublicPages/AddContacts.jsx
//
// POC public phone page reached via the invite QR/link:
//   /add-contacts?token=...
// Adds contacts into an organisation's default contact pool.
// Android Chrome/Edge: one-tap multi-select via the Contact Picker API.
// Everything else: manual rows (still batch — add many, submit once).

import React, { useEffect, useState } from "react";
import toast, { Toaster } from "react-hot-toast";

import {
    getContactInviteContext,
    submitContactInvite,
} from "js/Api/contact_invite";

function useQueryToken() {
    const [token] = useState(() => {
        const p = new URLSearchParams(window.location.search);
        return p.get("token") || "";
    });
    return token;
}

const emptyRow = () => ({ displayName: "", phoneNumberRaw: "" });

function AddContacts() {
    const token = useQueryToken();

    const [loading, setLoading] = useState(true);
    const [context, setContext] = useState(null);
    const [error, setError] = useState("");
    const [rows, setRows] = useState([emptyRow()]);
    const [submitting, setSubmitting] = useState(false);
    const [done, setDone] = useState(null);

    const contactPickerSupported =
        typeof navigator !== "undefined" &&
        "contacts" in navigator &&
        "select" in navigator.contacts;

    useEffect(() => {
        (async () => {
            const res = await getContactInviteContext(token);
            if (res.success) setContext(res.data);
            else setError(res.error || "This link is invalid or has expired.");
            setLoading(false);
        })();
    }, [token]);

    const updateRow = (i, field, value) => {
        setRows((prev) =>
            prev.map((r, idx) => (idx === i ? { ...r, [field]: value } : r))
        );
    };

    const addRow = () => setRows((prev) => [...prev, emptyRow()]);
    const removeRow = (i) =>
        setRows((prev) => (prev.length === 1 ? prev : prev.filter((_, idx) => idx !== i)));

    const pickFromPhone = async () => {
        try {
            const selected = await navigator.contacts.select(["name", "tel"], {
                multiple: true,
            });
            if (!selected || selected.length === 0) return;
            const picked = selected.map((c) => ({
                displayName: (c.name && c.name[0]) || "",
                phoneNumberRaw: (c.tel && c.tel[0]) || "",
            }));
            setRows((prev) => {
                // Replace the initial empty single row, otherwise append.
                const base =
                    prev.length === 1 && !prev[0].displayName && !prev[0].phoneNumberRaw
                        ? []
                        : prev;
                return [...base, ...picked];
            });
            toast.success(`${picked.length} contact(s) added from your phone`);
        } catch (e) {
            // User cancelled or API blocked — silent.
        }
    };

    const handleSubmit = async () => {
        const clean = rows
            .map((r) => ({
                displayName: (r.displayName || "").trim(),
                phoneNumberRaw: (r.phoneNumberRaw || "").trim(),
            }))
            .filter((r) => r.displayName && r.phoneNumberRaw);

        if (clean.length === 0) {
            toast.error("Add at least one contact with a name and number.");
            return;
        }

        setSubmitting(true);
        const res = await submitContactInvite(token, clean);
        setSubmitting(false);

        if (res.success) {
            setDone(res.data);
        } else {
            toast.error(res.error || "Failed to submit contacts.");
        }
    };

    // ── styles (mobile-first, dependency-free) ──
    const S = {
        page: {
            minHeight: "100vh",
            background: "#f4f6fb",
            padding: "20px 16px 40px",
            fontFamily: "system-ui, -apple-system, Segoe UI, Roboto, sans-serif",
        },
        card: {
            maxWidth: 520,
            margin: "0 auto",
            background: "#fff",
            borderRadius: 14,
            boxShadow: "0 6px 24px rgba(0,0,0,0.08)",
            padding: 20,
        },
        h1: { fontSize: 20, fontWeight: 700, margin: "0 0 4px", color: "#1a1a2e" },
        sub: { fontSize: 14, color: "#666", margin: "0 0 20px" },
        row: {
            display: "flex",
            gap: 8,
            marginBottom: 10,
            alignItems: "center",
        },
        input: {
            flex: 1,
            padding: "10px 12px",
            border: "1.5px solid #e0e0e0",
            borderRadius: 8,
            fontSize: 15,
            outline: "none",
            minWidth: 0,
        },
        del: {
            background: "#fef2f2",
            color: "#dc2626",
            border: "none",
            borderRadius: 8,
            width: 38,
            height: 38,
            fontSize: 18,
            cursor: "pointer",
            flexShrink: 0,
        },
        btn: {
            width: "100%",
            padding: "12px 16px",
            border: "none",
            borderRadius: 10,
            fontSize: 15,
            fontWeight: 600,
            cursor: "pointer",
        },
    };

    if (loading) {
        return (
            <div style={S.page}>
                <div style={S.card}>
                    <p style={{ textAlign: "center", color: "#666" }}>Loading…</p>
                </div>
            </div>
        );
    }

    if (error || !context) {
        return (
            <div style={S.page}>
                <div style={S.card}>
                    <h1 style={S.h1}>Link unavailable</h1>
                    <p style={S.sub}>{error || "This link is invalid or has expired."}</p>
                </div>
            </div>
        );
    }

    if (done) {
        return (
            <div style={S.page}>
                <Toaster position="top-center" />
                <div style={S.card}>
                    <h1 style={S.h1}>Thank you!</h1>
                    <p style={S.sub}>
                        {done.added} contact(s) added to{" "}
                        <strong>{context.orgName}</strong>
                        {done.skipped > 0
                            ? ` — ${done.skipped} skipped (duplicates or incomplete).`
                            : "."}
                    </p>
                    <button
                        style={{ ...S.btn, background: "#1976d2", color: "#fff" }}
                        onClick={() => {
                            setRows([emptyRow()]);
                            setDone(null);
                        }}
                    >
                        Add more
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div style={S.page}>
            <Toaster position="top-center" />
            <div style={S.card}>
                <h1 style={S.h1}>Add contacts</h1>
                <p style={S.sub}>
                    These will be added to <strong>{context.orgName}</strong>’s
                    contact list.
                </p>

                {contactPickerSupported && (
                    <button
                        style={{
                            ...S.btn,
                            background: "#10b981",
                            color: "#fff",
                            marginBottom: 18,
                        }}
                        onClick={pickFromPhone}
                    >
                        📇 Pick from my phone contacts
                    </button>
                )}

                {rows.map((r, i) => (
                    <div key={i} style={S.row}>
                        <input
                            style={S.input}
                            placeholder="Name"
                            value={r.displayName}
                            onChange={(e) => updateRow(i, "displayName", e.target.value)}
                        />
                        <input
                            style={S.input}
                            placeholder="Phone number"
                            inputMode="tel"
                            value={r.phoneNumberRaw}
                            onChange={(e) => updateRow(i, "phoneNumberRaw", e.target.value)}
                        />
                        <button
                            style={S.del}
                            onClick={() => removeRow(i)}
                            title="Remove"
                            disabled={rows.length === 1}
                        >
                            ×
                        </button>
                    </div>
                ))}

                <button
                    style={{
                        ...S.btn,
                        background: "#eef2ff",
                        color: "#1d4ed8",
                        marginTop: 6,
                        marginBottom: 18,
                    }}
                    onClick={addRow}
                >
                    + Add another
                </button>

                <button
                    style={{
                        ...S.btn,
                        background: submitting ? "#94a3b8" : "#1976d2",
                        color: "#fff",
                    }}
                    onClick={handleSubmit}
                    disabled={submitting}
                >
                    {submitting ? "Submitting…" : "Submit contacts"}
                </button>
            </div>
        </div>
    );
}

export default AddContacts;
